import { Observable, catchError, from, map, of } from 'rxjs'
import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'

import {
  AvanceMes,
  BankChoice,
  CapturaAvance,
  Descarga,
  HojaDelLibro,
  LigaComplementos,
  BankStatementParseResult,
  CarteraView,
  CentralImportResult,
  CentralView,
  CentralWatchVerdict,
  CollectionSeries,
  CuadreRentas,
  MonthlyCloseResult,
  MoraReport,
} from '../../interfaces/f098.interface'
import { environment } from '@env/environment'

type Wrapped<T> = { error: boolean; msg: string; data: T | null }

/** El `ResponseInterceptor` de la API envuelve TODO en `{error, msg, data}`; aquí se desenvuelve una sola vez. */
const fallo =
  <T>(msg: string) =>
  (e: any): Observable<Wrapped<T>> =>
    of({ error: true, msg: mensajeLegible(e, msg), data: null })

/**
 * El mensaje que se enseña. El del servidor cuando dice algo; si es un 500 genérico o no hay conexión,
 * uno que diga qué hacer: «Internal server error» no le sirve a nadie.
 */
function mensajeLegible(e: any, porOmision: string): string {
  if (e?.status === 0) return 'No hay conexión con el servidor. Revisa tu red y vuelve a intentar.'
  const delServidor = e?.error?.msg
  if (e?.status >= 500 && (!delServidor || /internal server error/i.test(String(delServidor)))) {
    return `${porOmision}: el servidor tuvo un error inesperado. Lo que ya se haya guardado se queda; vuelve a intentar y, si se repite, repórtalo con el botón de problemas.`
  }
  return delServidor || porOmision
}

/** El nombre del archivo en `Content-Disposition` (viene codificado: lleva acentos y espacios). */
export function nombreDeDescarga(cabecera: string | null): string | null {
  const m = cabecera?.match(/filename="?([^";]+)"?/i)
  if (!m) return null
  try {
    return decodeURIComponent(m[1])
  } catch {
    return m[1]
  }
}

/** El mensaje del servidor dentro de un error de descarga, aunque el cuerpo llegue como Blob. */
export async function mensajeDeError(e: any): Promise<string> {
  if (e?.status === 0) return 'No hay conexión con el servidor. Revisa tu red y vuelve a intentar.'
  if (e?.status === 401 || e?.status === 403) return 'No tienes permiso para descargar este reporte.'
  const cuerpo = e?.error
  try {
    const texto = cuerpo instanceof Blob ? await cuerpo.text() : typeof cuerpo === 'string' ? cuerpo : JSON.stringify(cuerpo ?? {})
    const msg = JSON.parse(texto)?.msg ?? JSON.parse(texto)?.message
    if (msg) return Array.isArray(msg) ? msg.join(' · ') : String(msg)
  } catch {
    //* El cuerpo no era JSON: se cae al mensaje genérico con el código.
  }
  return `No se pudo generar el archivo (error ${e?.status ?? 'desconocido'}). Intenta de nuevo; si se repite, repórtalo.`
}

/**
 * Lo que las vistas de F-098 le piden a la API: CENTRAL, el cuadre, la cartera, el avance, la mora y
 * la conciliación bancaria.
 *
 * Va aparte de `ApiCobranzaService` —que solo trae el embudo del encabezado— porque esto es de las
 * VISTAS, no del cascarón, y meterlo ahí haría que la pantalla entera dependiera de endpoints que
 * casi nunca usa.
 *
 * **Ningún método lanza.** Todos devuelven el sobre con `error: true` y un mensaje en español: una
 * vista que se rompe por un 500 deja al operador sin pantalla y sin explicación.
 */
@Injectable({ providedIn: 'root' })
export class ApiF098Service {
  private readonly http = inject(HttpClient)

  private get base(): string {
    return environment.apiUrl
  }

  // ── CENTRAL ────────────────────────────────────────────────────────────────────────────────

  central(month?: string): Observable<Wrapped<CentralView>> {
    return this.http.get(`${this.base}invoices/central${month ? `?month=${month}` : ''}`).pipe(
      map((r) => r as Wrapped<CentralView>),
      catchError(fallo<CentralView>('No se pudo cargar CENTRAL')),
    )
  }

  /** ¿Está subida la hoja del mes? Una CENTRAL vacía se ve igual que un mes sin facturas. */
  centralWatch(month?: string): Observable<Wrapped<CentralWatchVerdict>> {
    return this.http.get(`${this.base}invoices/central/watch${month ? `?month=${month}` : ''}`).pipe(
      map((r) => r as Wrapped<CentralWatchVerdict>),
      catchError(fallo<CentralWatchVerdict>('No se pudo comprobar si la hoja está subida')),
    )
  }

  cuadre(month?: string): Observable<Wrapped<CuadreRentas>> {
    return this.http.get(`${this.base}invoices/central/cuadre${month ? `?month=${month}` : ''}`).pipe(
      map((r) => r as Wrapped<CuadreRentas>),
      catchError(fallo<CuadreRentas>('No se pudo cargar el cuadre de rentas')),
    )
  }

  // ── Reportes contables (estructura del libro de Contabilidad) ─────────────────────────────

  /** El libro del mes: CARTERA, CENTRAL, AVANCE, DASHBOARD y CUADRE, más rastro, pagaré y notas. */
  libro(month?: string): Observable<Descarga> {
    return this.descargar('get', `accounting-reports/libro${month ? `?month=${month}` : ''}`)
  }

  /** Una sola hoja del libro, con sus notas. Mismo generador que el libro: no hay dos versiones. */
  hoja(hoja: HojaDelLibro, month?: string): Observable<Descarga> {
    return this.descargar('get', `accounting-reports/hoja/${hoja}${month ? `?month=${month}` : ''}`)
  }

  /** COMPLEMENTOS del mes: el layout del timbrador y el control por grupos. */
  complementos(month?: string): Observable<Descarga> {
    return this.descargar('get', `accounting-reports/complementos/export${month ? `?month=${month}` : ''}`)
  }

  /** REPORTE DE CLIENTES EN MORA a una fecha de corte (`YYYY-MM-DD`). */
  clientesEnMora(asOf?: string): Observable<Descarga> {
    return this.descargar('get', `accounting-reports/clientes-en-mora/export${asOf ? `?asOf=${asOf}` : ''}`)
  }

  /** La conciliación del estado de cuenta en .xlsx, con la estructura de su hoja. */
  conciliacionExport(file: File, bank: BankChoice = 'AUTO', month?: string): Observable<Descarga> {
    const fd = new FormData()
    fd.append('file', file, file.name)
    return this.descargar('post', `accounting-reports/conciliacion/export?bank=${bank}${month ? `&month=${month}` : ''}`, fd)
  }

  // ── Avance capturado ─────────────────────────────────────────────────────────────────────

  avanceDelAnio(month?: string): Observable<Wrapped<AvanceMes[]>> {
    return this.http.get(`${this.base}accounting-reports/avance${month ? `?month=${month}` : ''}`).pipe(
      map((r) => r as Wrapped<AvanceMes[]>),
      catchError(fallo<AvanceMes[]>('No se pudo cargar el avance del año')),
    )
  }

  capturas(from?: string, to?: string): Observable<Wrapped<CapturaAvance[]>> {
    const q = [from ? `from=${from}` : '', to ? `to=${to}` : ''].filter(Boolean).join('&')
    return this.http.get(`${this.base}accounting-reports/captures${q ? `?${q}` : ''}`).pipe(
      map((r) => r as Wrapped<CapturaAvance[]>),
      catchError(fallo<CapturaAvance[]>('No se pudieron cargar las capturas')),
    )
  }

  guardarCaptura(month: string, dto: { total: number | null; collected: number | null; pending: number | null; reason?: string | null }): Observable<Wrapped<CapturaAvance>> {
    return this.http.patch(`${this.base}accounting-reports/captures/avance/${month}`, dto).pipe(
      map((r) => r as Wrapped<CapturaAvance>),
      catchError(fallo<CapturaAvance>('No se pudo guardar la captura')),
    )
  }

  quitarCaptura(id: string, reason: string): Observable<Wrapped<CapturaAvance>> {
    return this.http.delete(`${this.base}accounting-reports/captures/${id}`, { body: { reason } }).pipe(
      map((r) => r as Wrapped<CapturaAvance>),
      catchError(fallo<CapturaAvance>('No se pudo dar de baja la captura')),
    )
  }

  /** Liga los complementos existentes con su pago. Sin `apply` solo dice qué haría. */
  ligarComplementos(apply: boolean): Observable<Wrapped<LigaComplementos>> {
    return this.http.post(`${this.base}invoices/complements/link-payments${apply ? '?apply=true' : ''}`, {}).pipe(
      map((r) => r as Wrapped<LigaComplementos>),
      catchError(fallo<LigaComplementos>('No se pudieron ligar los complementos')),
    )
  }

  /**
   * Pide un archivo y devuelve el blob con el nombre que manda el servidor.
   *
   *! Cuando falla, el cuerpo del error también llega como Blob (es lo que se pidió) y adentro viene el
   *! `{msg}` del servidor en español. Tirarlo y decir «no se pudo generar» dejaba a Contabilidad sin
   *! saber si era el mes, el archivo o un permiso. Se lee y se devuelve.
   */
  private descargar(metodo: 'get' | 'post', ruta: string, body?: FormData): Observable<Descarga> {
    const peticion =
      metodo === 'get'
        ? this.http.get(`${this.base}${ruta}`, { responseType: 'blob', observe: 'response' })
        : this.http.post(`${this.base}${ruta}`, body, { responseType: 'blob', observe: 'response' })
    return peticion.pipe(
      map((res) => ({ blob: res.body, fileName: nombreDeDescarga(res.headers.get('Content-Disposition')), msg: null })),
      catchError((e) => from(mensajeDeError(e)).pipe(map((msg) => ({ blob: null, fileName: null, msg })))),
    )
  }

  /** La importación de la hoja: previsualizar NO escribe; importar sí. */
  centralPreview(file: File, sheetName?: string): Observable<Wrapped<CentralImportResult>> {
    return this.http.post(`${this.base}invoices/central/preview`, this.form(file, sheetName)).pipe(
      map((r) => r as Wrapped<CentralImportResult>),
      catchError(fallo<CentralImportResult>('No se pudo leer la hoja')),
    )
  }

  centralImport(file: File, sheetName?: string): Observable<Wrapped<CentralImportResult>> {
    return this.http.post(`${this.base}invoices/central/import`, this.form(file, sheetName)).pipe(
      map((r) => r as Wrapped<CentralImportResult>),
      catchError(fallo<CentralImportResult>('No se pudo importar la hoja')),
    )
  }

  // ── Lo que Contabilidad captura a mano ─────────────────────────────────────────────────────

  /**
   * Corrige la clasificación, el mes contable o el vencimiento de una factura.
   *
   * Son los datos que su hoja tiene y la plataforma no sabe derivar. Cada cambio queda en la
   * bitácora de la factura con su autor.
   */
  patchAccounting(invoiceId: string, dto: { classification?: string; month?: string; dueDate?: string | null; notes?: string | null; reason?: string }): Observable<Wrapped<any>> {
    return this.http.patch(`${this.base}invoices/${invoiceId}/accounting`, dto).pipe(
      map((r) => r as Wrapped<any>),
      catchError(fallo<any>('No se pudo corregir la factura')),
    )
  }

  /** Registra un depósito capturado: el respaldo de lo que la plataforma no concilia sola. */
  addDeposit(invoiceId: string, dto: { amount: number; bank?: string | null; reference?: string | null; paidAt?: string | null }): Observable<Wrapped<any>> {
    return this.http.post(`${this.base}invoices/${invoiceId}/deposits`, dto).pipe(
      map((r) => r as Wrapped<any>),
      catchError(fallo<any>('No se pudo registrar el depósito')),
    )
  }

  removeDeposit(invoiceId: string, depositId: string): Observable<Wrapped<any>> {
    return this.http.delete(`${this.base}invoices/${invoiceId}/deposits/${depositId}`).pipe(
      map((r) => r as Wrapped<any>),
      catchError(fallo<any>('No se pudo quitar el depósito')),
    )
  }

  // ── CARTERA ────────────────────────────────────────────────────────────────────────────────

  cartera(month?: string): Observable<Wrapped<CarteraView>> {
    return this.http.get(`${this.base}wallet/cartera${month ? `?month=${month}` : ''}`).pipe(
      map((r) => r as Wrapped<CarteraView>),
      catchError(fallo<CarteraView>('No se pudo cargar la cartera del mes')),
    )
  }

  /** Dice qué congelaría el cierre. No escribe nada. */
  closePreview(month: string): Observable<Wrapped<MonthlyCloseResult>> {
    return this.http.get(`${this.base}wallet/cartera/close/preview?month=${month}`).pipe(
      map((r) => r as Wrapped<MonthlyCloseResult>),
      catchError(fallo<MonthlyCloseResult>('No se pudo previsualizar el cierre')),
    )
  }

  close(month: string): Observable<Wrapped<MonthlyCloseResult>> {
    return this.http.post(`${this.base}wallet/cartera/close`, { month }).pipe(
      map((r) => r as Wrapped<MonthlyCloseResult>),
      catchError(fallo<MonthlyCloseResult>('No se pudo cerrar el mes')),
    )
  }

  /** Reabrir exige un motivo: queda en cada fotografía y es lo que explica por qué cambió. */
  reopen(month: string, reason: string): Observable<Wrapped<{ month: string; reopened: number }>> {
    return this.http.post(`${this.base}wallet/cartera/reopen`, { month, reason }).pipe(
      map((r) => r as Wrapped<{ month: string; reopened: number }>),
      catchError(fallo<{ month: string; reopened: number }>('No se pudo reabrir el mes')),
    )
  }

  // ── Avance y mora ──────────────────────────────────────────────────────────────────────────

  series(from?: string, to?: string): Observable<Wrapped<CollectionSeries>> {
    const q = [from ? `from=${from}` : '', to ? `to=${to}` : ''].filter(Boolean).join('&')
    return this.http.get(`${this.base}payments/collection-series${q ? `?${q}` : ''}`).pipe(
      map((r) => r as Wrapped<CollectionSeries>),
      catchError(fallo<CollectionSeries>('No se pudo cargar la serie de cobranza')),
    )
  }

  moraReport(asOf?: string): Observable<Wrapped<MoraReport>> {
    return this.http.get(`${this.base}interest/mora-report${asOf ? `?asOf=${asOf}` : ''}`).pipe(
      map((r) => r as Wrapped<MoraReport>),
      catchError(fallo<MoraReport>('No se pudo cargar el reporte de mora')),
    )
  }

  // ── Conciliación bancaria ──────────────────────────────────────────────────────────────────

  /**
   * La hoja de carga de pagos, salida del mismo estado de cuenta.
   *
   * Se manda el archivo otra vez porque la conciliación **no guarda nada**: la pantalla ya lo tiene
   * en la mano y el servidor lo vuelve a leer. Es el precio de que esto no persista, y es barato.
   */
  bankStatementCarga(file: File, bank: BankChoice = 'AUTO'): Observable<Blob | null> {
    const fd = new FormData()
    fd.append('file', file, file.name)
    return this.http.post(`${this.base}payments/bank-statement/carga?bank=${bank}`, fd, { responseType: 'blob' }).pipe(catchError(() => of(null)))
  }

  bankStatement(file: File, bank: BankChoice = 'AUTO'): Observable<Wrapped<BankStatementParseResult>> {
    const fd = new FormData()
    fd.append('file', file, file.name)
    return this.http.post(`${this.base}payments/bank-statement?bank=${bank}`, fd).pipe(
      map((r) => r as Wrapped<BankStatementParseResult>),
      catchError(fallo<BankStatementParseResult>('No se pudo leer el estado de cuenta')),
    )
  }

  private form(file: File, sheetName?: string): FormData {
    const fd = new FormData()
    fd.append('file', file, file.name)
    if (sheetName) fd.append('sheetName', sheetName)
    return fd
  }
}
